import { useMutation } from '@apollo/client';
import React, {
  createContext, useCallback, useEffect, useState,
} from 'react';
import * as Keychain from 'react-native-keychain';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import jwt_decode from 'jwt-decode';
import { setTokenRefreshHandler } from '../lib/tokenRefreshBridge';
import { RefreshTokenMutation } from '../gql/user/mutation';
import { ROLE_NAMES } from '../utils/constant';
import { stopBackgroundLocationTracking } from '../services/backgroundLocation';

const STORAGE_KEY_AUTH_TOKEN = '@auth_token';
const STORAGE_KEY_PARENT_SESSION = '@parent_session';

const storeTokenForBackgroundTask = async (accessToken) => {
  if (Platform.OS !== 'web' && accessToken) {
    try {
      await AsyncStorage.setItem(STORAGE_KEY_AUTH_TOKEN, accessToken);
    } catch (e) { /* silently fail */ }
  }
};

const clearBackgroundToken = async () => {
  if (Platform.OS !== 'web') {
    try {
      await AsyncStorage.removeItem(STORAGE_KEY_AUTH_TOKEN);
    } catch (e) { /* silently fail */ }
  }
};

const saveActiveSession = async (session) => {
  if (Platform.OS === 'web') {
    localStorage.setItem('email', session.email);
    localStorage.setItem('accessToken', session.accessToken);
    localStorage.setItem('refreshToken', session.refreshToken);
    localStorage.setItem('username', session.username);
    localStorage.setItem('role', session.role);
    localStorage.setItem('canBrowseNas', String(session.canBrowseNas ?? false));
    localStorage.setItem('accountSelected', String(session.accountSelected ?? true));
  } else {
    await Keychain.setGenericPassword('token', JSON.stringify(session));
    await storeTokenForBackgroundTask(session.accessToken);
  }
};

const loadActiveSession = async () => {
  if (Platform.OS === 'web') {
    return {
      email: localStorage.getItem('email'),
      accessToken: localStorage.getItem('accessToken'),
      refreshToken: localStorage.getItem('refreshToken'),
      username: localStorage.getItem('username'),
      role: localStorage.getItem('role'),
      canBrowseNas: localStorage.getItem('canBrowseNas') === 'true',
      accountSelected: localStorage.getItem('accountSelected') !== 'false',
    };
  }
  const value = await Keychain.getGenericPassword();
  if (value) return JSON.parse(value.password);
  return null;
};

const saveParentSession = async (session) => {
  try {
    await AsyncStorage.setItem(STORAGE_KEY_PARENT_SESSION, JSON.stringify(session));
    if (Platform.OS === 'web') {
      localStorage.setItem('parentSession', JSON.stringify(session));
    }
  } catch (e) { /* silently fail */ }
};

const loadParentSession = async () => {
  try {
    if (Platform.OS === 'web') {
      const raw = localStorage.getItem('parentSession');
      return raw ? JSON.parse(raw) : null;
    }
    const raw = await AsyncStorage.getItem(STORAGE_KEY_PARENT_SESSION);
    return raw ? JSON.parse(raw) : null;
  } catch (e) { return null; }
};

const clearParentSession = async () => {
  try {
    await AsyncStorage.removeItem(STORAGE_KEY_PARENT_SESSION);
    if (Platform.OS === 'web') localStorage.removeItem('parentSession');
  } catch (e) { /* silently fail */ }
};

const AuthContext = createContext();
const { Provider } = AuthContext;

function AuthProvider({ children }) {
  const [refreshToken] = useMutation(RefreshTokenMutation);

  const [authState, setAuthState] = useState({
    email: null,
    username: null,
    accessToken: null,
    refreshToken: null,
    authenticated: false,
    role: null,
    canBrowseNas: false,
    notificationToken: null,
    // account switching
    accountSelected: true,
    parentSession: null,   // set when acting as child
    isActingAsChild: false,
  });

  const logout = async () => {
    if (Platform.OS === 'web') {
      ['email', 'accessToken', 'refreshToken', 'username', 'role', 'canBrowseNas', 'accountSelected', 'parentSession']
        .forEach((k) => localStorage.setItem(k, null));
    } else {
      await Keychain.resetGenericPassword();
      await clearBackgroundToken();
      await stopBackgroundLocationTracking();
    }
    await clearParentSession();
    setAuthState({
      email: null,
      username: null,
      role: null,
      canBrowseNas: false,
      accessToken: null,
      refreshToken: null,
      authenticated: false,
      accountSelected: true,
      parentSession: null,
      isActingAsChild: false,
    });
  };

  // Called by LoginScreen after a successful login response
  const setLoginSession = useCallback(async ({ accessToken, refreshToken: rt, user }) => {
    const hasChildren = user.children?.length > 0;
    const session = {
      email: user.email,
      accessToken,
      refreshToken: rt,
      authenticated: true,
      username: `${user.firstName} ${user.lastName}`,
      role: user.role,
      canBrowseNas: user.canBrowseNas ?? false,
      accountSelected: !hasChildren, // if children exist, force the picker
    };
    await saveActiveSession(session);
    setAuthState({
      ...session,
      notificationToken: authState.notificationToken,
      parentSession: null,
      isActingAsChild: false,
    });
  }, [authState.notificationToken]);

  // Called when parent picks "continue as child"
  const switchToChild = useCallback(async ({ accessToken, refreshToken: rt, user }) => {
    const parentSnapshot = {
      email: authState.email,
      accessToken: authState.accessToken,
      refreshToken: authState.refreshToken,
      username: authState.username,
      role: authState.role,
      canBrowseNas: authState.canBrowseNas,
    };
    await saveParentSession(parentSnapshot);

    const childSession = {
      email: user.email,
      accessToken,
      refreshToken: rt,
      authenticated: true,
      username: `${user.firstName} ${user.lastName}`,
      role: user.role,
      canBrowseNas: user.canBrowseNas ?? false,
      accountSelected: true,
    };
    await saveActiveSession(childSession);

    setAuthState({
      ...childSession,
      notificationToken: authState.notificationToken,
      parentSession: parentSnapshot,
      isActingAsChild: true,
    });
  }, [authState]);

  // Called when parent switches back to their own account
  const switchToParent = useCallback(async () => {
    const parent = authState.parentSession;
    if (!parent) return;

    const parentSession = {
      ...parent,
      authenticated: true,
      accountSelected: true,
    };
    await saveActiveSession(parentSession);
    await clearParentSession();

    setAuthState({
      ...parentSession,
      notificationToken: authState.notificationToken,
      parentSession: null,
      isActingAsChild: false,
    });
  }, [authState]);

  // Mark account as selected (parent chose to stay as themselves)
  const markAccountSelected = useCallback(async () => {
    const updated = { ...authState, accountSelected: true };
    await saveActiveSession({
      email: authState.email,
      accessToken: authState.accessToken,
      refreshToken: authState.refreshToken,
      username: authState.username,
      role: authState.role,
      canBrowseNas: authState.canBrowseNas,
      authenticated: true,
      accountSelected: true,
    });
    setAuthState(updated);
  }, [authState]);

  // Keep authState in sync when Apollo's errorLink silently refreshes tokens
  useEffect(() => {
    setTokenRefreshHandler(({ accessToken, refreshToken: rt }) => {
      setAuthState((prev) => ({ ...prev, accessToken, refreshToken: rt }));
    });
  }, []);

  const getAccessToken = () => authState.accessToken;
  const isSuperAdmin = authState.role === ROLE_NAMES.SUPERADMIN;
  const isCoach = authState.role === ROLE_NAMES.COACH;

  const loadJWT = useCallback(async () => {
    try {
      if (Platform.OS !== 'web') {
        await stopBackgroundLocationTracking();
      }

      const jwt = await loadActiveSession();
      if (!jwt?.accessToken) throw new Error('No stored session');

      // Check if we were acting as child
      const parentSnapshot = await loadParentSession();

      const handleExpired = async () => {
        if (!jwt.refreshToken || jwt_decode(jwt.refreshToken).exp < Date.now() / 1000) {
          throw new Error('Refresh token expired');
        }
        const { data: refreshData } = await refreshToken({
          variables: { refreshToken: jwt.refreshToken, notificationToken: authState.notificationToken },
        });
        if (!refreshData?.refreshToken) throw new Error('Failed to refresh token');

        const newSession = {
          email: refreshData.refreshToken.user.email,
          accessToken: refreshData.refreshToken.accessToken,
          refreshToken: refreshData.refreshToken.refreshToken,
          authenticated: true,
          username: `${refreshData.refreshToken.user.firstName} ${refreshData.refreshToken.user.lastName}`,
          role: refreshData.refreshToken.user.role,
          canBrowseNas: refreshData.refreshToken.user.canBrowseNas ?? false,
          accountSelected: true,
        };
        await saveActiveSession(newSession);
        setAuthState({
          ...newSession,
          notificationToken: authState.notificationToken,
          parentSession: parentSnapshot,
          isActingAsChild: !!parentSnapshot,
        });
      };

      if (jwt_decode(jwt.accessToken).exp < Date.now() / 1000) {
        await handleExpired();
        return;
      }

      await storeTokenForBackgroundTask(jwt.accessToken);
      setAuthState({
        email: jwt.email || null,
        accessToken: jwt.accessToken || null,
        refreshToken: jwt.refreshToken || null,
        authenticated: jwt.accessToken !== null,
        username: jwt.username || null,
        role: jwt.role || null,
        canBrowseNas: jwt.canBrowseNas ?? false,
        accountSelected: jwt.accountSelected ?? true,
        notificationToken: authState.notificationToken,
        parentSession: parentSnapshot,
        isActingAsChild: !!parentSnapshot,
      });
    } catch (error) {
      console.error(`loadJWT error: ${error.message}`);
      setAuthState({
        email: null,
        accessToken: null,
        refreshToken: null,
        authenticated: false,
        role: null,
        canBrowseNas: false,
        accountSelected: true,
        parentSession: null,
        isActingAsChild: false,
      });
    }
  }, [refreshToken, authState.notificationToken]);

  return (
    <Provider
      value={{
        authState,
        getAccessToken,
        setAuthState,
        logout,
        loadJWT,
        isSuperAdmin,
        isCoach,
        setLoginSession,
        switchToChild,
        switchToParent,
        markAccountSelected,
      }}
    >
      {children}
    </Provider>
  );
}

export { AuthContext, AuthProvider };
